import { getCompetitorEffectVariables } from '@/dashboards/CompetitorsMediaInvestment/helpers/getCompetitorEffectVariables';
import { useExecutiveSummaryContext } from '@/dashboards/ExecutiveSummary/context';
import {
    calculateCompetitorContributionProductTotals,
    type CompetitorContributionProductTotals,
} from '@/dashboards/ExecutiveSummary/helpers/calculateCompetitorContributionProduct';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { spendSummaryQueries } from '@/duckdb/spend-summary';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type { CompetitorContributionProductTotals };

export interface CompetitorContributionProductData
    extends CompetitorContributionProductTotals {}

export interface UseCompetitorContributionProductQueryResult {
    isLoading: boolean;
    isFetching: boolean;
    data: CompetitorContributionProductData;
}

/**
 * Competitor Contribution × Cost — the numerator ROI needs for the "Total
 * Competitor {ROI}" card, mirroring `useGeneralSummarySpendQuery`'s
 * `totalContributionProduct` but scoped to competitor variables.
 *
 * Reuses `spendSummaryQueries.getRowsWithContributionProduct` (the same
 * Contribution × Cost formula from the active ROI config), restricted to the
 * output-file competitor variables also used by `useCompetitorImpactQuery`.
 */
export const useCompetitorContributionProductQuery =
    (): UseCompetitorContributionProductQueryResult => {
        const { primaryRange, comparisonRange } = useExecutiveSummaryContext();
        const {
            project: { roiType, id: projectId },
            dashboard: { dashboardVariables },
        } = useDashboardGlobalContext();

        const competitorEffectVariables = useMemo(
            () => getCompetitorEffectVariables(dashboardVariables),
            [dashboardVariables],
        );

        const hasCompetitorVariables = competitorEffectVariables.length > 0;

        const buildQueryOptions = (range?: {
            startDate?: string;
            endDate?: string;
        }) => {
            const hasRange = Boolean(range?.startDate && range?.endDate);
            return {
                queryKey: [
                    'competitorContributionProduct',
                    projectId,
                    range?.startDate,
                    range?.endDate,
                    roiType,
                    competitorEffectVariables,
                ],
                queryFn: () =>
                    spendSummaryQueries.getRowsWithContributionProduct({
                        projectId,
                        period: 'yearly',
                        startDate: range?.startDate,
                        endDate: range?.endDate,
                        roiType,
                        variables: competitorEffectVariables,
                    }),
                enabled: hasCompetitorVariables && hasRange,
            };
        };

        const primaryQueryResult = useQuery(buildQueryOptions(primaryRange));
        const comparisonQueryResult = useQuery(
            buildQueryOptions(comparisonRange),
        );

        const data = useMemo<CompetitorContributionProductData>(
            () =>
                calculateCompetitorContributionProductTotals(
                    primaryQueryResult.data,
                    comparisonQueryResult.data,
                ),
            [primaryQueryResult.data, comparisonQueryResult.data],
        );

        return {
            isLoading:
                primaryQueryResult.isLoading ||
                comparisonQueryResult.isLoading,
            isFetching:
                primaryQueryResult.isFetching ||
                comparisonQueryResult.isFetching,
            data,
        };
    };
